import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, Users, Clock } from 'lucide-react';

interface ProblemSectionProps {
  companyName?: string;
}

export const ProblemSection: React.FC<ProblemSectionProps> = ({ 
  companyName = "votre entreprise" 
}) => {
  const problems = [
    {
      icon: TrendingDown,
      title: "Trafic insuffisant",
      description: "Votre site web ne génère pas assez de visiteurs qualifiés pour alimenter votre croissance business.",
      stat: "87% des sites B2B"
    },
    {
      icon: Users,
      title: "Leads non qualifiés",
      description: "Les visiteurs arrivent sur votre site mais ne se transforment pas en prospects intéressés par vos services.",
      stat: "Taux de conversion < 2%"
    },
    {
      icon: Clock,
      title: "Site trop lent",
      description: "Un site lent fait fuir vos prospects. Chaque seconde de chargement supplémentaire = -7% de conversions.",
      stat: "3 sec = 53% d'abandon"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22%23f97316%22%20fill-opacity%3D%220.03%22%20fill-rule%3D%22evenodd%22%3E%3Cpath%20d%3D%22M20%2020c0%2011.046-8.954%2020-20%2020s-20-8.954-20-20%208.954-20%2020-20%2020%208.954%2020%2020zm-20-18c-9.941%200-18%208.059-18%2018s8.059%2018%2018%2018%2018-8.059%2018-18-8.059-18-18-18z%22/%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Diagnostic Performance
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Votre site ne génère pas assez de{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                leads qualifiés
              </span>{' '}
              ?
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comme 87% des entreprises B2B, {companyName} fait probablement face à ces défis critiques 
              qui limitent votre croissance digitale.
            </p>
          </motion.div>

          {/* Problems Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                  {/* Problem Icon */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <problem.icon className="w-8 h-8 text-orange-600" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                  </div>

                  {/* Problem Content */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {problem.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {problem.description}
                    </p>
                  </div>

                  {/* Problem Stat */}
                  <div className="mt-auto">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-50 border border-red-200">
                      <span className="text-red-700 text-sm font-semibold">
                        {problem.stat}
                      </span>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Impact Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden"
          >
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-orange-400/5 rounded-full blur-2xl"></div>

            <div className="relative z-10">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 text-sm font-medium mb-6">
                <TrendingDown className="w-4 h-4 mr-2" />
                Impact Business
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ces problèmes vous coûtent{' '}
                <span className="text-orange-400">des milliers d'euros</span>{' '}
                chaque mois
              </h3>

              <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                Chaque jour sans optimisation = opportunités perdues, prospects qui partent chez la concurrence, 
                et un ROI marketing qui stagne.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400 mb-2">-67%</div>
                  <div className="text-gray-400 text-sm">Leads potentiels perdus</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400 mb-2">-45%</div>
                  <div className="text-gray-400 text-sm">ROI marketing gaspillé</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400 mb-2">-89%</div>
                  <div className="text-gray-400 text-sm">Visibilité Google manquée</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
